import * as Sentry from '@sentry/browser';
import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import { useAuthenticationStatus } from './hooks/useAuthenticationStatus';
import { useAppDispatch, useAppSelector } from './store';
import { isLoading } from './store/agents/selectors';
import { setErrorObject } from './store/globalErrors/reducer';
import { getUser } from './store/User/actions';
import { sendToast } from './utils/helpers';
import { signOutHandler } from './utils/signOutHandler';

const Modal = lazy(() => import('./components/Modal/Modal'));
const MapPage = lazy(() => import('./pages/MapPage'));

interface PrivateRouteProps {
  isAuthenticated: boolean;
}

const PrivateRoute = ({ isAuthenticated }: PrivateRouteProps) =>
  isAuthenticated ? <Outlet /> : null;

const App = () => {
  const dispatch = useAppDispatch();
  const [isAuthenticated, authLoading] = useAuthenticationStatus();
  const loading = useAppSelector((state) => isLoading(state));
  const { user } = useAppSelector((state) => state.users);
  const { errorObject } = useAppSelector((state) => state.globalErrors);

  useEffect(() => {
    if (errorObject) {
      sendToast(errorObject.message, {
        type: 'error',
        autoClose: false,
        closeOnClick: false,
      });
      dispatch(setErrorObject(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorObject]);

  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      Sentry.setUser(null);
      signOutHandler();
    }

    if (isAuthenticated && !authLoading && !user) {
      dispatch(getUser());
    }

    if (isAuthenticated && !authLoading && user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, isAuthenticated]);

  const showPrivateRoutes = !authLoading && isAuthenticated;
  const mapShell = showPrivateRoutes ? (
    <Suspense fallback={<main className="h-screen bg-[var(--mode-bg)] " />}>
      <MapPage loading={loading} />
    </Suspense>
  ) : null;

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<PrivateRoute isAuthenticated={isAuthenticated} />}>
          <Route path="/" element={mapShell} />
          <Route path="/map" element={mapShell} />
          <Route path="/deal/:dealId" element={mapShell} />
          <Route path="*" element={<Navigate to="/map" replace />} />
        </Route>
      </Routes>
      <Suspense>
        <Modal />
      </Suspense>
    </BrowserRouter>
  );
};

export default App;
